import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import {
  LayoutDashboard, UserPlus, LogOut, Settings, BarChart3, History, MessageSquare, Megaphone, Layers, Award
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SuperAdminSidebarProps {
  onInviteClick?: () => void;
}

const SuperAdminSidebar: React.FC<SuperAdminSidebarProps> = ({ onInviteClick }) => {
  const auth = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!auth.user?.email) return 'SA';
    return auth.user.email
      .split('@')[0]
      .split('.')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isRouteActive = (path: string) => {
    return location.pathname === path || (location.pathname.startsWith(path + '/') && path !== '/events');
  };

  const menuItems = [
    { path: '/events', title: 'Dashboard', icon: LayoutDashboard },
    { path: '/events/global-ps', title: 'Global PS Repositories', icon: Layers },
    { path: '/events/sthitih-mentors', title: 'Sthitih Mentors', icon: Award },
    { path: '/events/support', title: 'Support Tickets', icon: MessageSquare },
    { path: '/events/announcements', title: 'Announcements', icon: Megaphone },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 w-full h-16 bg-[#F7F2EA] border-t border-[#DDD0BE] flex flex-row items-center justify-around z-40 md:w-20 md:h-screen md:top-0 md:bottom-auto md:left-0 md:border-r md:border-t-0 md:flex-col md:justify-between md:py-6 overflow-visible shadow-earth-sm">
      {/* Top Menu Items (Desktop: vertical; Mobile: horizontal) */}
      <div className="flex flex-row md:flex-col items-center justify-around md:justify-start w-full md:space-y-4">
        {/* Top spacer matching header height on desktop only */}
        <div className="hidden md:block h-16 flex-shrink-0" />

        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isRouteActive(item.path);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-all relative group cursor-pointer ${
                active
                  ? 'bg-[#1F5A43] text-white md:bg-[#1F5A43]/10 md:text-[#1F5A43] shadow-xs font-bold'
                  : 'text-[#8D8A83] hover:bg-[#EFE5D6] hover:text-[#1F5A43]'
              }`}
              title={item.title}
              aria-label={item.title}
            >
              <Icon className="w-5 h-5" />
              {/* Tooltip on Desktop */}
              <span className="hidden md:block absolute left-24 bg-[#FFFDF9] text-[#243238] text-xs font-semibold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-earth border border-[#E4D8C8]">
                {item.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bottom User/Action Section (Desktop: bottom; Mobile: inline at right) */}
      <div className="relative flex flex-row md:flex-col items-center gap-2 md:gap-4 md:pb-4 overflow-visible shrink-0 pr-2 md:pr-0">
        {/* Invite Tenant Button */}
        {onInviteClick && (
          <button
            onClick={onInviteClick}
            className="w-10 h-10 md:w-12 md:h-12 rounded-xl text-[#8D8A83] hover:bg-[#EFE5D6] hover:text-[#1F5A43] flex items-center justify-center transition-all relative group cursor-pointer"
            title="Invite Tenant"
            aria-label="Invite Tenant"
          >
            <UserPlus className="w-5 h-5" />
            <span className="hidden md:block absolute left-24 bg-[#FFFDF9] text-[#243238] text-xs font-semibold px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-earth border border-[#E4D8C8]">
              Invite Tenant
            </span>
          </button>
        )}

        <button
          ref={userButtonRef}
          onClick={() => setShowUserMenu(!showUserMenu)}
          className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
            showUserMenu ? 'bg-[#EFE5D6]' : 'hover:bg-[#EFE5D6]'
          }`}
          title="User Account"
          aria-label="User Account"
        >
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-[#1F5A43] text-white font-bold flex items-center justify-center text-xs md:text-sm shadow-md">
            {getUserInitials()}
          </div>
        </button>

        {/* Popover User Menu */}
        <AnimatePresence>
          {showUserMenu && (
            <motion.div
              ref={userMenuRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed bottom-20 right-4 md:bottom-6 md:right-auto md:left-24 w-64 bg-[#FFFDF9] rounded-[18px] shadow-2xl py-3 border border-[#E4D8C8] z-[999]"
            >
              <div className="px-4 py-2 border-b border-[#E4D8C8] mb-2">
                <p className="text-xs font-bold text-[#243238] truncate">Super Admin</p>
                <p className="text-[10px] text-[#6F706C] truncate mt-0.5">{auth.user?.email}</p>
              </div>

              <div className="px-2 space-y-1">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/events/reports'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#243238] hover:bg-[#EFE5D6] rounded-xl transition-colors text-left cursor-pointer"
                >
                  <BarChart3 className="w-3.5 h-3.5 text-[#1F5A43]" />
                  <span>Reports</span>
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/events/audit-logs'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#243238] hover:bg-[#EFE5D6] rounded-xl transition-colors text-left cursor-pointer"
                >
                  <History className="w-3.5 h-3.5 text-[#1F5A43]" />
                  <span>Audit Logs</span>
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#243238] hover:bg-[#EFE5D6] rounded-xl transition-colors text-left cursor-pointer"
                >
                  <Settings className="w-3.5 h-3.5 text-[#1F5A43]" />
                  <span>Settings</span>
                </button>
                
                <div className="border-t border-[#E4D8C8] my-1" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-[#D64545] hover:bg-[#FCE8E8] rounded-xl transition-colors text-left cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5 text-[#D64545]" />
                  <span>Sign Out</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default SuperAdminSidebar;
